/* eslint-disable @typescript-eslint/no-unused-vars */
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import LinkIcon from "@mui/icons-material/Link";
import { Box, ButtonGroup, IconButton } from "@mui/material";
import { createChatGptRequest } from "@platformx/authoring-apis";
import { handleHtmlTags, PROJECT_CATCH_ERROR_LIST, trimString } from "@platformx/utilities";
import DOMPurify from "isomorphic-dompurify";
import { lazy, memo, Suspense, useEffect, useRef, useState } from "react";
import { htmlToPlainText } from "../../Utils/helperFunction";
import DescriptionContentCard from "../DescriptionContentCard";
import AddUrlDialog from "../url-dialog-box/AddUrlDialog";
import ChatGptLoader from "./ChatGptLoader";
import "./editor.css";
import "./remove-icon-styles.css";

const DEFAULT_DESC_MAX_LENGTH = 12000;
const DESC_MAX_LENGTH =
  Number(process.env.NX_PUBLIC_ARTICLE_DESC_MAX_LENGTH) || DEFAULT_DESC_MAX_LENGTH;

const LazyBasicLexicalEditor = lazy(() =>
  import("@platformx/lexical-editor").then((module) => ({ default: module.BasicLexicalEditor })),
);

const LazyAiAssistDialog = lazy(() =>
  import("@platformx/lexical-editor").then((module) => ({ default: module.AiAssistDialog })),
);

function Description({ title, state, setState, setCheckDesc }) {
  const currentLine = useRef<any>("");
  const desc = useRef<any>("");
  const [showToolbar, setShowToolbar] = useState(false);
  const [left, setLeft] = useState(0);
  const [top, setTop] = useState(0);
  const [isUrlDialog, setUrlDialog] = useState(false);
  const [description, setDescription] = useState<any>("");
  const updateTempObj = useRef<any>({});
  const [isLoading, setIsLoading] = useState(false);
  const [ifLink] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const editorRef = useRef<any>(null);

  const [selectedTextData, setSelectedTextData] = useState("");
  const open = Boolean(anchorEl);
  const selectionOffsets = useRef<{ start: number; end: number } | null>(null);

  const handlePlaceholder = (e) => {
    const ele = document.getElementById("desc");
    const placeholder = ele?.getAttribute("data-placeholder");
    if (ele?.innerHTML === "") {
      ele.innerHTML = placeholder || "";
    }
    const value = e.target.innerHTML;
    if (value === placeholder) {
      e.target.innerHTML = "";
    }
  };

  const handleTextSelection = (e) => {
    const selection = document.getSelection();
    const selectedText = selection?.toString() || "";

    const { pageX } = e;
    const { pageY } = e;
    if (selectedText?.trim() !== "") {
      setTop(pageY - 60);
      setLeft(pageX - 55);
      setShowToolbar(true);
      updateTempObj.current = selection?.getRangeAt(0);
      setSelectedTextData(selectedText);
    } else {
      setShowToolbar(false);
      setSelectedTextData("");
    }
  };

  useEffect(() => {
    const dataHolder = document.getElementById("desc");
    dataHolder?.addEventListener("focus", handlePlaceholder);
    dataHolder?.addEventListener("mouseup", handleTextSelection);
    return () => {
      dataHolder?.removeEventListener("mouseup", handleTextSelection);
      dataHolder?.removeEventListener("focus", handlePlaceholder);
    };
  }, []);

  const onBoldClick = () => {
    document.execCommand("bold", false, undefined);
    setShowToolbar(false);
  };

  const onItalicClick = () => {
    document.execCommand("italic", false, undefined);
    setShowToolbar(false);
  };

  const onUnderlineClick = () => {
    document.execCommand("underline", false, undefined);
    setShowToolbar(false);
  };

  const onBlockquoteClick = () => {
    if (document.queryCommandValue("FormatBlock") === "blockquote") {
      document.execCommand("FormatBlock", false, "<div>");
    } else {
      document.execCommand("FormatBlock", false, "<blockquote>");
    }
    setShowToolbar(false);
  };

  const onClickDone = (url) => {
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(updateTempObj.current);
    if (url) {
      document.execCommand(
        "insertHTML",
        false,
        `<a href="${url}" target="_blank">${selection}</a>`,
      );
    }
    setUrlDialog(false);
  };

  const onUrlDialogOpen = () => {
    if (ifLink) {
      document.execCommand("unLink", false, `${updateTempObj.current}`);
      setShowToolbar(false);
    } else {
      setUrlDialog(true);
      setShowToolbar(false);
    }
  };

  const onClickClose = () => {
    setUrlDialog(false);
  };

  const handleDescriptionChange = (htmlContent: string) => {
    setDescription(htmlContent);
    desc.current = htmlContent;

    if (!showToolbar) {
      setState({
        ...state,
        CommonFields: {
          ...state.CommonFields,
          description: htmlContent,
          settings: {
            ...state.CommonFields.settings,
            socialog_description: trimString(handleHtmlTags(htmlContent), 200),
          },
        },
      });
    }
  };

  const blockQuotes = `
      blockquote{
        margin: 0;
        border-left: 3px solid black;
        padding: 5px 10px;
        font-style:italic;
      }
      .placeholdertext[placeholder]:empty::before {
        content: attr(placeholder);
        color: #ced3d9;
        font-size: 18px;
        line-height: 1.8;
    }
   
    .placeholdertext[placeholder]:empty:focus::before {
        content: "";
    }
      `;

  const LanguageNameMapping = {
    en: "English",
    fr: "French",
    de: "German",
  };

  const chatGptResponse = (prompt) => {
    const requestParam = {
      input: {
        prompt: prompt,
      },
    };
    createChatGptRequest(requestParam)
      .then((result: any) => {
        const chatGptDataResponse = result?.authoring_getContentOpenai?.text;
        const dataAdded = chatGptDataResponse.replace(/(\r\n|\n|\r)/gm, "<br>");
        if (chatGptDataResponse) {
          setIsLoading(false);
          const el = document.getElementById("desc");
          if (el !== null) {
            el.innerHTML = el.innerHTML + dataAdded;
          }
          desc.current = desc.current + dataAdded;
          setState({
            ...state,
            CommonFields: {
              ...state.CommonFields,
              description: document?.getElementById("desc")?.innerHTML,
              settings: {
                ...state.CommonFields.settings,
                socialog_description: trimString(handleHtmlTags(chatGptDataResponse), 200),
              },
            },
          });
          setDescription(document?.getElementById("desc")?.innerHTML);
          setCheckDesc(document?.getElementById("desc")?.innerHTML);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        console.error(`${PROJECT_CATCH_ERROR_LIST.authoring}:chatGptResponse`, err);
        throw err;
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const chatGPT = () => {
    setIsLoading(true);
    const chatGptPrompt = "Please write 2 paragraph, max 100 words article about";
    chatGptResponse(`${chatGptPrompt} ${title} in ${LanguageNameMapping["en"]}`);
  };

  useEffect(() => {
    if (Object.keys(state).length > 0) {
      setDescription(state?.CommonFields?.description);
    }
  }, [state]);

  function highlightSelection() {
    document.querySelectorAll("#desc .ai-highlight").forEach((el) => {
      if (el instanceof HTMLElement) {
        el.classList.remove("ai-highlight");
        el.style.backgroundColor = "";

        if (el.tagName !== "P" && !el.className && !el.style.cssText) {
          const parent = el.parentNode;
          while (el.firstChild) {
            parent?.insertBefore(el.firstChild, el);
          }
          parent?.removeChild(el);
        }
      }
    });

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    if (range.collapsed) return;

    const { startContainer, endContainer } = range;
    if (startContainer === endContainer && startContainer.nodeType === Node.TEXT_NODE) {
      const span = document.createElement("span");
      span.className = "ai-highlight";
      span.style.backgroundColor = "#E9F2FF";
      range.surroundContents(span);
    }
  }

  const handleAIClick = (event) => {
    highlightSelection();
    setAnchorEl(event.currentTarget);
  };

  const handleReplaceSelectedText = (aiText: string) => {
    const descEl = document.getElementById("desc");
    if (!descEl) return;

    const formattedText = aiText
      .split(/\n\s*\n/)
      .map((paragraph) => paragraph.trim())
      .filter((paragraph) => paragraph.length > 0)
      .map((paragraph) => {
        return paragraph.replace(/\n/g, "<br>");
      })
      .join("<br><br>");

    document.querySelectorAll("#desc .ai-highlight").forEach((el) => {
      if (el instanceof HTMLElement) {
        el.classList.remove("ai-highlight");
        el.style.backgroundColor = "";

        if (el.tagName !== "P" && !el.className && !el.style.cssText) {
          const parent = el.parentNode;
          while (el.firstChild) {
            parent?.insertBefore(el.firstChild, el);
          }
          parent?.removeChild(el);
        }
      }
    });

    descEl.focus();

    let selection = window.getSelection();
    if (
      updateTempObj.current &&
      typeof updateTempObj.current.collapsed === "boolean" &&
      typeof updateTempObj.current.setStart === "function"
    ) {
      selection?.removeAllRanges();
      selection?.addRange(updateTempObj.current);
    }

    selection = window.getSelection();
    let range: Range | null = null;
    if (selection && selection.rangeCount > 0) {
      range = selection.getRangeAt(0);
    }

    let newHtml = "";

    const isFullSelection =
      range &&
      range.startContainer === descEl &&
      range.startOffset === 0 &&
      range.endContainer === descEl &&
      range.endOffset === descEl.childNodes.length;

    if (isFullSelection || !selectedTextData) {
      descEl.innerHTML = `<span class="ai-highlight" style="background-color:#E9F2FF;">${formattedText}</span><br>`;
      newHtml = descEl.innerHTML;
      const span = descEl.querySelector(".ai-highlight");
      if (span) {
        const caretRange = document.createRange();
        caretRange.selectNodeContents(span);
        caretRange.collapse(false);
        selection?.removeAllRanges();
        selection?.addRange(caretRange);
      }
    } else if (range && !range.collapsed && descEl.contains(range.startContainer)) {
      range.deleteContents();

      const span = document.createElement("span");
      span.style.backgroundColor = "#E9F2FF";
      span.className = "ai-highlight";
      span.innerHTML = formattedText;
      range.insertNode(span);

      const newRange = document.createRange();
      newRange.setStartAfter(span);
      newRange.collapse(true);
      selection?.removeAllRanges();
      selection?.addRange(newRange);

      newHtml = descEl.innerHTML;
    } else {
      const span = document.createElement("span");
      span.style.backgroundColor = "#E9F2FF";
      span.className = "ai-highlight";
      span.innerHTML = formattedText;

      const lastBr = Array.from(descEl.childNodes)
        .reverse()
        .find((node) => node.nodeName === "BR");

      if (lastBr) {
        descEl.insertBefore(span, lastBr);
        descEl.insertBefore(document.createElement("br"), lastBr);
      } else {
        descEl.appendChild(span);
        descEl.appendChild(document.createElement("br"));
      }

      const caretRange = document.createRange();
      caretRange.selectNodeContents(span);
      caretRange.collapse(false);
      selection?.removeAllRanges();
      selection?.addRange(caretRange);

      newHtml = descEl.innerHTML;
    }

    setDescription(newHtml);
    desc.current = newHtml;
    setShowToolbar(false);
    setSelectedTextData("");
    selectionOffsets.current = null;
    updateTempObj.current = null;
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    function removeHighlight(e: MouseEvent) {
      const descBox = document.getElementById("desc");
      const path = e.composedPath ? e.composedPath() : [];

      const clickedInsideDialog = path.some(
        (el) =>
          el instanceof HTMLElement &&
          (el.classList?.contains("ai-dialog-paper-root") ||
            el.hasAttribute?.("data-ai-dialog-root")),
      );

      const clickedOnHighlight = path.some(
        (el) => el instanceof HTMLElement && el.classList && el.classList.contains("ai-highlight"),
      );

      const clickedInsideEditor = descBox && path.includes(descBox);

      if (clickedInsideDialog || clickedOnHighlight) {
        return;
      }

      let hasChanges = false;

      if (clickedInsideEditor) {
        document.querySelectorAll("#desc .ai-highlight").forEach((el) => {
          if (el instanceof HTMLElement) {
            el.classList.remove("ai-highlight");
            el.style.backgroundColor = "";
            hasChanges = true;

            if (el.tagName !== "P" && !el.className && !el.style.cssText) {
              const parent = el.parentNode;
              while (el.firstChild) {
                parent?.insertBefore(el.firstChild, el);
              }
              parent?.removeChild(el);
            }
          }
        });

        if (hasChanges && descBox) {
          const newHtml = descBox.innerHTML;
          setDescription(newHtml);
          desc.current = newHtml;
          setState({
            ...state,
            CommonFields: {
              ...state.CommonFields,
              description: newHtml,
              settings: {
                ...state.CommonFields.settings,
                socialog_description: trimString(handleHtmlTags(newHtml), 200),
              },
            },
          });
        }
        return;
      }

      document.querySelectorAll("#desc .ai-highlight").forEach((el) => {
        if (el instanceof HTMLElement) {
          el.classList.remove("ai-highlight");
          el.style.backgroundColor = "";
          hasChanges = true;

          if (el.tagName !== "P" && !el.className && !el.style.cssText) {
            const parent = el.parentNode;
            while (el.firstChild) {
              parent?.insertBefore(el.firstChild, el);
            }
            parent?.removeChild(el);
          }
        }
      });

      if (hasChanges && descBox) {
        const newHtml = descBox.innerHTML;
        setDescription(newHtml);
        desc.current = newHtml;
        setState({
          ...state,
          CommonFields: {
            ...state.CommonFields,
            description: newHtml,
            settings: {
              ...state.CommonFields.settings,
              socialog_description: trimString(handleHtmlTags(newHtml), 200),
            },
          },
        });
      }
    }

    document.addEventListener("mousedown", removeHighlight);

    return () => {
      document.removeEventListener("mousedown", removeHighlight);
    };
  }, [setState, state]);

  const handleInsertBelow = (aiText: string) => {
    const descEl = document.getElementById("desc");
    if (!descEl) return;

    const currentContent = descEl.textContent || descEl.innerText || "";
    const isEmpty = currentContent.trim().length === 0;

    if (isEmpty) {
      const formattedText = aiText
        .split(/\n\s*\n/)
        .map((paragraph) => paragraph.trim())
        .filter((paragraph) => paragraph.length > 0)
        .map((paragraph) => {
          return paragraph.replace(/\n/g, "<br>");
        })
        .join("<br><br>");

      const sanitized = DOMPurify.sanitize(formattedText, { ADD_ATTR: ["target"] });

      const span = document.createElement("span");
      span.className = "ai-highlight";
      span.style.backgroundColor = "#E9F2FF";
      span.innerHTML = sanitized;

      descEl.innerHTML = "";
      descEl.appendChild(span);
    } else {
      const formattedText = aiText
        .split(/\n\s*\n/)
        .map((paragraph) => paragraph.trim())
        .filter((paragraph) => paragraph.length > 0)
        .map((paragraph) => {
          const content = paragraph.replace(/\n/g, "<br>");
          return `<p>${content}</p>`;
        })
        .join("");

      const sanitized = DOMPurify.sanitize(formattedText, { ADD_ATTR: ["target"] });

      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = sanitized;

      Array.from(tempDiv.children).forEach((child) => {
        if (child.nodeType === 1) {
          (child as HTMLElement).classList.add("ai-highlight");
          (child as HTMLElement).style.backgroundColor = "#E9F2FF";
        }
      });

      while (tempDiv.firstChild) {
        descEl.appendChild(tempDiv.firstChild);
      }
    }

    const newHtml = descEl.innerHTML;
    setDescription(newHtml);
    desc.current = newHtml;

    setState({
      ...state,
      CommonFields: {
        ...state.CommonFields,
        description: newHtml,
        settings: {
          ...state.CommonFields.settings,
          socialog_description: trimString(handleHtmlTags(newHtml), 200),
        },
      },
    });

    setShowToolbar(false);
    setSelectedTextData("");
    selectionOffsets.current = null;
    updateTempObj.current = null;
  };

  const text = htmlToPlainText(document?.getElementById("desc")?.innerHTML ?? "");
  const hasDescription = text.trim().length > 0;

  return (
    <>
      {isLoading && <ChatGptLoader />}
      <Box
        sx={{
          backgroundColor: "#FFF",
        }}>
        <style>{blockQuotes}</style>
      </Box>
      <Box
        sx={{
          display: "flex",
          fontSize: "16px",
          backgroundColor: "white",
          width: "100%",
          position: "relative",
        }}>
        <Suspense fallback={null}>
          <LazyBasicLexicalEditor
            ref={editorRef}
            data={description || ""}
            onChange={handleDescriptionChange}
            title={title}
            descMaxLength={DESC_MAX_LENGTH}
            showAssetPicker={true}
            showContentPicker={true}
            DescriptionContentCard={DescriptionContentCard}
            fullHeight={true}
          />
        </Suspense>

        <ButtonGroup
          className='tools'
          id='toolbar'
          style={{
            display: showToolbar ? "inline-flex" : "none",
            backgroundColor: "black",
            left,
            top,
            position: "fixed",
          }}>
          <IconButton onClick={onBoldClick} data-testid='article-bold-icon' sx={{ color: "white" }}>
            <FormatBoldIcon />
          </IconButton>
          <IconButton
            onClick={onItalicClick}
            data-testid='article-italic-icon'
            sx={{ color: "white" }}>
            <FormatItalicIcon />
          </IconButton>
          <IconButton
            onClick={onUnderlineClick}
            data-testid='article-underline-icon'
            sx={{ color: "white" }}>
            <FormatUnderlinedIcon />
          </IconButton>
          <IconButton
            onClick={onUrlDialogOpen}
            data-testid='article-open-url-dialog'
            sx={{ color: "white" }}>
            <LinkIcon />
          </IconButton>
          <IconButton
            onClick={onBlockquoteClick}
            data-testid='article-block-quote'
            sx={{ color: "white" }}>
            <FormatQuoteIcon />
          </IconButton>
        </ButtonGroup>
      </Box>
      {isUrlDialog ? (
        <AddUrlDialog
          titledata=''
          isDialogOpen={isUrlDialog}
          closeButtonHandle={onClickClose}
          data-testid='article-url-dialog-close'
          doneButtonHandle={onClickDone}
        />
      ) : null}
      {open && (
        <Suspense fallback={null}>
          <LazyAiAssistDialog
            anchorEl={anchorEl}
            open={open}
            handleClose={handleClose}
            hasDescription={hasDescription}
            descMaxLength={1000}
            title={title}
            context={selectedTextData || text}
            onAIResponse={handleReplaceSelectedText}
            handleInsertBelow={handleInsertBelow}
          />
        </Suspense>
      )}
    </>
  );
}
export default memo(Description);
